import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import apiService from '../services/api';
import { User, LoginRequest, Token, TokenPayload, UserCreate } from '../types/models';
import axios from 'axios';
import { API_URL } from '../config/api';
import emailVerificationService from '../services/emailVerification';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isBuyer: boolean;
  isSeller: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: UserCreate) => Promise<User>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
  error: string | null;
  // Email verification methods
  isEmailVerified: boolean;
  resendVerificationEmail: () => Promise<void>;
  getVerificationStatus: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check if token is valid and fetch user data on mount
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Verify token is valid
          const payload = jwtDecode<TokenPayload>(token);
          const currentTime = Math.floor(Date.now() / 1000);
          
          if (payload.exp < currentTime) {
            // Token expired
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            setLoading(false);
            return;
          }
          
          // Fetch user data
          const userData = await apiService.get<User>('/users/me');
          setUser(userData);
        } catch (err) {
          console.error('Auth initialization error:', err);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (data: LoginRequest) => {
    setLoading(true);
    setError(null);
    try {
      // Create URLSearchParams for OAuth2 password flow
      const params = new URLSearchParams();
      params.append('username', data.username);
      params.append('password', data.password);
      

      
      // Use axios directly for this request since we need to send form data
      const response = await axios.post(
        `${API_URL}/login/access-token`,
        params,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      

      
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Fetch user data after successful login
      const userData = await apiService.get<User>('/users/me');
      setUser(userData);
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
      }
      
      // Make sure error is a string
      const errorMessage = err.response?.data?.detail 
        ? typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail)
        : 'Login failed. Please check your credentials.';
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: UserCreate): Promise<User> => {
    setLoading(true);
    setError(null);
    try {
      // Use the new email verification registration endpoint
      const newUser = await emailVerificationService.register(data);
      // Registration successful, return user data but don't log in automatically
      return newUser;
    } catch (err: any) {
      // Make sure error is a string
      const errorMessage = err.message || 'Error en el registro. Intenta nuevamente.';
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Email verification methods
  const resendVerificationEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      await emailVerificationService.resendVerification();
      // Refresh user data to update verification status
      await refreshUser();
    } catch (err: any) {
      const errorMessage = err.message || 'Error al reenviar email de verificación';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getVerificationStatus = async () => {
    try {
      return await emailVerificationService.getVerificationStatus();
    } catch (err: any) {
      console.error('Error getting verification status:', err);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await apiService.get<User>('/users/me');
      setUser(userData);
    } catch (err) {
      console.error('Error refreshing user:', err);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.is_admin || false,
    isBuyer: user?.is_buyer || false,
    isSeller: user?.is_seller || false,
    login,
    register,
    logout,
    refreshUser,
    loading,
    error,
    // Email verification properties and methods
    isEmailVerified: user?.email_verified || false,
    resendVerificationEmail,
    getVerificationStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
